import apiClient from './client';
import type {
  EmployeeSkillEntry,
  SkillHeatmapEntry,
  HeatmapDetailEntry,
  SkillDiffResult,
  SkillHistoryEntry,
  DevelopmentGoalEntry,
  GoalSuggestion,
} from '../types';

// ---- Skill Names ----

export async function fetchSkillNames(): Promise<string[]> {
  const { data } = await apiClient.get<string[]>('/skill-development/skill-names');
  return data;
}

// ---- Skill Proficiency ----

export async function fetchEmployeeSkills(employeeId: string): Promise<EmployeeSkillEntry[]> {
  const { data } = await apiClient.get<EmployeeSkillEntry[]>(
    `/skill-development/employees/${employeeId}/skills`,
  );
  return data;
}

export async function updateSkillProficiency(
  employeeId: string,
  skillName: string,
  proficiency: number,
  evidence?: string,
): Promise<EmployeeSkillEntry> {
  const { data } = await apiClient.put<EmployeeSkillEntry>(
    `/skill-development/employees/${employeeId}/skills`,
    { proficiency, evidence, source: 'MANUAL' },
    { params: { skillName } },
  );
  return data;
}

// ---- Skill Heatmap ----

export async function fetchSkillHeatmap(groupBy: string = 'department'): Promise<SkillHeatmapEntry[]> {
  const { data } = await apiClient.get<SkillHeatmapEntry[]>(
    '/skill-development/heatmap',
    { params: { groupBy } },
  );
  return data;
}

export async function fetchHeatmapDetail(
  groupBy: string,
  group: string,
  category: string,
): Promise<HeatmapDetailEntry[]> {
  const { data } = await apiClient.get<HeatmapDetailEntry[]>(
    '/skill-development/heatmap/detail',
    { params: { groupBy, group, category } },
  );
  return data;
}

// ---- Skill History & Diff ----

export async function fetchSkillHistory(employeeId: string): Promise<SkillHistoryEntry[]> {
  const { data } = await apiClient.get<SkillHistoryEntry[]>(
    `/skill-development/employees/${employeeId}/skill-history`,
  );
  return data;
}

export async function fetchSkillDiff(employeeId: string): Promise<SkillDiffResult> {
  const { data } = await apiClient.get<SkillDiffResult>(
    `/skill-development/employees/${employeeId}/skill-diff`,
  );
  return data;
}

// ---- Development Goals ----

export async function fetchEmployeeGoals(employeeId: string): Promise<DevelopmentGoalEntry[]> {
  const { data } = await apiClient.get<DevelopmentGoalEntry[]>(
    `/skill-development/employees/${employeeId}/goals`,
  );
  return data;
}

export async function createGoal(
  employeeId: string,
  goal: {
    skillName: string;
    currentLevel: number;
    targetLevel: number;
    targetDate?: string;
    notes?: string;
    source?: string;
    personaId?: string;
  },
): Promise<DevelopmentGoalEntry> {
  const { data } = await apiClient.post<DevelopmentGoalEntry>(
    `/skill-development/employees/${employeeId}/goals`,
    goal,
  );
  return data;
}

export async function updateGoal(
  goalId: string,
  updates: {
    status?: string;
    targetLevel?: number;
    targetDate?: string;
    notes?: string;
  },
): Promise<DevelopmentGoalEntry> {
  const { data } = await apiClient.put<DevelopmentGoalEntry>(
    `/skill-development/goals/${goalId}`,
    updates,
  );
  return data;
}

export async function deleteGoal(goalId: string): Promise<void> {
  await apiClient.delete(`/skill-development/goals/${goalId}`);
}

export async function fetchGoalSuggestions(employeeId: string): Promise<GoalSuggestion[]> {
  const { data } = await apiClient.get<GoalSuggestion[]>(
    `/skill-development/employees/${employeeId}/goal-suggestions`,
  );
  return data;
}
